import {Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output} from '@angular/core';
import {Observable, Subscription} from 'rxjs';

@Component({
  selector: 'app-expandable-panel',
  templateUrl: './expandable-panel.component.html',
  styleUrls: ['./expandable-panel.component.scss']
})
export class ExpandablePanelComponent implements OnDestroy {
  /** CSS selector of the closest ancestor the expanded panel should cover. */
  @Input() boundary = '.expand-boundary, app-tab';
  /** Collapses the panel whenever this emits, e.g. when the user switches to another workspace tab. */
  @Input() set collapseOn(source: Observable<any>) {
    this.collapseSubscription?.unsubscribe();
    this.collapseSubscription = source ? source.subscribe(() => this.collapse()) : undefined;
  }
  @Output() expandedChange = new EventEmitter<boolean>();

  public expanded = false;
  /** Position of the covered region; null means cover the whole window. */
  public area: { top: number, left: number, width: number, height: number } | null = null;
  private collapseSubscription: Subscription;
  private resizeObserver: ResizeObserver;
  private boundaryElement: Element | null = null;

  constructor(private host: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  toggle() {
    this.setExpanded(!this.expanded);
  }

  collapse() {
    this.setExpanded(false);
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.collapse();
  }

  @HostListener('window:resize')
  updateArea() {
    if (!this.expanded) {
      return;
    }
    if (!this.boundaryElement) {
      this.area = null;
      return;
    }
    const rect = this.boundaryElement.getBoundingClientRect();
    this.area = {top: rect.top, left: rect.left, width: rect.width, height: rect.height};
  }

  ngOnDestroy(): void {
    if (this.expanded) {
      document.body.classList.remove('panel-expanded');
    }
    this.collapseSubscription?.unsubscribe();
    this.stopObserving();
  }

  private setExpanded(value: boolean) {
    if (this.expanded === value) {
      return;
    }
    this.expanded = value;
    if (value) {
      this.boundaryElement = this.boundary ? this.host.nativeElement.closest(this.boundary) : null;
      this.updateArea();
      this.startObserving();
    } else {
      this.stopObserving();
      this.area = null;
    }
    // Lets host pages hide surrounding chrome (tabs, side panels) while expanded.
    document.body.classList.toggle('panel-expanded', value);
    this.expandedChange.emit(value);
  }

  private startObserving() {
    if (!this.boundaryElement || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.updateArea()));
      this.resizeObserver.observe(this.boundaryElement);
    });
  }

  private stopObserving() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.boundaryElement = null;
  }
}
